import  { useMemo, useState, useCallback } from 'react';
import {
  ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar,
  IconButton, Typography, Box, Drawer, useMediaQuery, Dialog,
  DialogTitle, DialogActions, Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EmpowerCareNetwork from '../../EmpowerCareNetwork/EmpowerCareNetwork';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '../../utils/api';
import defaultLogo from '../../assets/logo.png'
import { useNavigate } from 'react-router-dom';
import CreateNewAdmin from '../../CreateNewAdmin/CreateNewAdmin';
import UpdateGoogleDrive from '../../UpdateGoogleDrive/UpdateGoogleDrive'
import UpdatePrompt from '../../UpdatePrompt/UpdatePrompt';
import UpdateApiKey from '../../UpdateApiKey/UpdateApiKey';
import UpdateResendKey from '../../UpdateResendKey/UpdateResendKey';
import AdminList from '../../CreateNewAdmin/AdminList';
import UpdateWebhooks from '../../UpdateWebhooks/UpdateWebhooks';
import UpdateGhlConnection from '../../UpdateGhlConnection/UpdateGhlConnection';
import SetupFields from '../../GHLCustomFields/SetupFields';
import LockedUsers from '../../LockedUsers/LockedUsers';
import PendingFollowups from '../../PendingFollowups/PendingFollowups';
import KioskManagement from '../../KioskManagement/KioskManagement';
import ActivityLogs from '../ActivityLogs/ActivityLogs';
import DashboardHome from './DashboardHome';
import PatientsList from '../Patients/PatientsList';
import { useTenant } from '../../context/TenantContext';
import logger from '../../utils/logger';


const drawerWidth = 240;

function Admin() {
  const [mode, setMode] = useState('light');
  const [selectedView, setSelectedView] = useState('dashboard');
  const [patientsFilter, setPatientsFilter] = useState('all');
  const [patientsDateRange, setPatientsDateRange] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Get the logo to display
  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  const theme = useMemo(() => createTheme({ palette: { mode, primary: { main: tenant?.primaryColor || '#06AB8B' }, secondary: { main: tenant?.secondaryColor || '#049572' } } }), [mode, tenant?.primaryColor, tenant?.secondaryColor]);

  // Check if screen is mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  }

  const handleLogoutClick = useCallback(() => {
    setLogoutDialogOpen(true);
  }, []);

  const handleLogoutConfirm = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await api.post('/api/super-admin-logout');
    } catch (err) {
      logger.error('Logout API failed:', err);
      // Continue with logout even if API fails
    } finally {
      sessionStorage.clear();
      localStorage.removeItem('superAdminToken'); // Clear stored JWT token
      setLogoutDialogOpen(false);
      setIsLoggingOut(false);

      // Check if we're on a custom domain
      const hostname = window.location.hostname;
      const isCustomDomain = hostname !== 'localhost' &&
                             !hostname.includes('127.0.0.1') &&
                             !hostname.includes('kinsta.page') &&
                             !hostname.includes('kinsta.app') &&
                             !hostname.includes('aptible');

      // If on custom domain, just redirect to / (domain identifies tenant)
      // If NOT on custom domain, preserve tenant parameter
      const searchParams = new URLSearchParams(window.location.search);
      const tenantParam = searchParams.get('tenant');
      const loginUrl = isCustomDomain ? '/' : (tenantParam ? `/?tenant=${tenantParam}` : '/');
      window.location.href = loginUrl;
    }
  }, []);

  // Render based on sidebar click
 const renderComponent = () => {
  switch (selectedView) {
    case 'dashboard':
      return <DashboardHome onNavigate={(filterKey, dateInfo) => { setPatientsFilter(filterKey); setPatientsDateRange(dateInfo || null); setSelectedView('patients'); }} />;
    case 'patients':
      return (
        <PatientsList initialFilter={patientsFilter} dateRange={patientsDateRange} onFilterUsed={() => setPatientsFilter('all')} onClearDateRange={() => setPatientsDateRange(null)} />
      );
    case 'create-new-admin':
      return <CreateNewAdmin/>;
    case 'update-google-drive':
      return <UpdateGoogleDrive/>;
    case 'update-api-key':
      return <UpdateApiKey/>;
    case 'update-resend-key':
      return <UpdateResendKey/>;
    case 'update-prompt':
      return <UpdatePrompt/>;
    case 'admin-list':
      return <AdminList/>;
    case 'ghl-connection':
      return <UpdateGhlConnection/>;
    case 'update-webhooks':
      return <UpdateWebhooks/>;
    case 'ghl-custom-fields':
      return <SetupFields/>;
    case 'pending-followups':
      return <PendingFollowups/>;
    case 'locked-users':
      return <LockedUsers/>;
    case 'kiosk-management':
      return <KioskManagement/>;
    case 'activity-logs':
      return <ActivityLogs/>;
    default:
      return <DashboardHome />;
  }
};

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#fff',
          color: '#333',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '60px', height: 'auto', maxHeight: '50px', objectFit: 'contain' }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleLogoutClick} title='Logout' sx={{ color: '#333' }}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Desktop drawer - permanent */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              marginTop: '64px', // Below AppBar height
              height: 'calc(100% - 64px)',
            },
          }}
        >
          <EmpowerCareNetwork selectedView={selectedView} setSelectedView={(view) => { if (view === 'patients') { setPatientsDateRange(null); setPatientsFilter('all'); } setSelectedView(view); }} />
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              marginTop: '56px', // Below AppBar height on mobile
              height: 'calc(100% - 56px)',
            },
          }}
        >
          <EmpowerCareNetwork selectedView={selectedView} setSelectedView={(view) => { if (view === 'patients') { setPatientsDateRange(null); setPatientsFilter('all'); } setSelectedView(view); }} />
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          px: { xs: 2, md: 4 },
          ml: { md: `${drawerWidth}px`, xs: 0 },
          mt: 8
        }}
      >
        {renderComponent()}
      </Box>

      <Dialog open={logoutDialogOpen} onClose={() => !isLoggingOut && setLogoutDialogOpen(false)}>
        <DialogTitle>Are you sure you want to logout?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            color="error"
            disabled={isLoggingOut}
          >
            No
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Logging out...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default Admin;
